import './App.css';
import { useMemo } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider/LocalizationProvider.js';
import { SnackbarProvider, useSnackbar } from 'notistack';
import { CacheProvider } from '@emotion/react';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import ThemeProvider, { createEmotionCache } from './Context/themeProvider.js';
import MainLayout from './Containers/MainLayout';
import { AuthContextProvider } from './Context/authContext';
import { FormContextProvider } from './Context/formContext';
import 'react-calendar/dist/Calendar.css';
import 'react-datepicker/dist/react-datepicker.css';
import { SENTRY_DSN, MIXPANEL_PROJECTID } from './Utils/Constants/constant.js';
import { ErrorFallback } from './Components/ErrorBoundary';
import mixpanel from './Utils/mixpanelClient';

// Detect prerender environment (react-snap sets userAgent to "ReactSnap")
const isPrerender = typeof navigator !== 'undefined' && typeof navigator.userAgent === 'string' && navigator.userAgent.toLowerCase().includes('reactsnap');

let sentryInit = null;
let sentryBrowserTracing = null;
let sentryReplay = null;
let SentryErrorBoundary = null;

if (!isPrerender) {
  // Defer loading Sentry until we know we're not prerendering to avoid proxy errors inside react-snap.
  // eslint-disable-next-line global-require
  const Sentry = require('@sentry/react');
  sentryInit = Sentry.init;
  sentryBrowserTracing = Sentry.browserTracingIntegration;
  sentryReplay = Sentry.replayIntegration;
  SentryErrorBoundary = Sentry.ErrorBoundary;
}

function SnackbarCloseButton({ snackbarKey }) {
  const { closeSnackbar } = useSnackbar();

  return (
    <IconButton onClick={() => closeSnackbar(snackbarKey)}>
      <CloseIcon sx={{ fontSize: '1rem', color: '#fff' }} />
    </IconButton>
  );
}

// Sentry and Mixpanel must be initialised exactly once. Running this from inside App's render
// re-ran it on every re-render, which made Sentry throw "Multiple Sentry Session Replay instances
// are not supported" and left the SDK uninitialised, so runtime errors were never reported.
function initTelemetry() {
  if (isPrerender || !sentryInit) {
    return;
  }
  try {
    sentryInit({
      dsn: SENTRY_DSN,
      integrations: [sentryBrowserTracing(), sentryReplay()],
      tracesSampleRate: 1.0,
      replaysSessionSampleRate: 0.1,
      replaysOnErrorSampleRate: 1.0,
      sendDefaultPii: true,
    });
  } catch (err) {
    console.error('Sentry initatization error', err);
  }
  if (typeof mixpanel === 'function') {
    try {
      //
      mixpanel.init(MIXPANEL_PROJECTID, {
        debug: true,
        track_pageview: true,
        autocapture: {
          pageview: 'full-url',
          click: true,
          input: true,
          submit: true,
        },
      });
    } catch (err) {
      console.error('Mixpanel initialization error', err);
    }
  }
}

initTelemetry();

function App() {
  const queryClient = useMemo(() => new QueryClient(), []);
  const clientSideEmotionCache = useMemo(() => createEmotionCache(), []);

  const appTree = (
    <CacheProvider value={clientSideEmotionCache}>
      <ThemeProvider>
        <QueryClientProvider client={queryClient}>
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <BrowserRouter>
              <FormContextProvider>
                <AuthContextProvider>
                  <div className="App">
                    {/* <ReactQueryDevtools initialIsOpen={false} /> */}
                    {/* <DynamicTitleSetter /> */}
                    {/* <MetadataHandler /> */}
                    <MainLayout />
                  </div>
                </AuthContextProvider>
              </FormContextProvider>
            </BrowserRouter>
          </LocalizationProvider>
        </QueryClientProvider>
      </ThemeProvider>
    </CacheProvider>
  );

  return (
    <SnackbarProvider autoHideDuration={5000} action={(snackbarKey) => <SnackbarCloseButton snackbarKey={snackbarKey} />}>
      {SentryErrorBoundary ? (
        <SentryErrorBoundary
          fallback={({ error, componentStack, resetError }) => (
            <ErrorFallback
              error={error}
              componentStack={componentStack}
              resetErrorBoundary={() => {
                resetError();
              }}
            />
          )}
        >
          {appTree}
        </SentryErrorBoundary>
      ) : (
        appTree
      )}
    </SnackbarProvider>
  );
}

export default App;
